import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { BCPUser } from "../icons/bcp/BCPIcons";
import { userDisplayName } from "../../pages/events/components/EventFunctions";
import { getData } from "../utilities/UtilityFunctions";
import { isAppMode } from "../../global/AppOverlay";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { useTranslation } from "react-i18next";
import { getLanguageCookie } from "../../language/LanguageFunctions";

export default function TeamCard(props) {
  const { team, highlight, organize, isAdmin, removeMember, setTeamAsDefault } =
    props;
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiTeamData, setApiTeamData] = useState(null);
  const theme = useTheme();
  const appMode = isAppMode();

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "buttons"
  });

  useEffect(() => {
    const fetchFileImage = async (fileId, setImageFunction) => {
      try {
        const imageData = await getData(`/files/${fileId}`);
        setImageFunction(imageData);
      } catch (err) {
        console.error(`Error fetching file ${fileId}:`, err);
        setImageFunction(null);
      }
    };

    const fetchTeamDetailsAndImage = async () => {
      if (!team?.id) {
        setPhoto(null);
        setApiTeamData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setPhoto(null);
      setApiTeamData(null);

      try {
        const currentTeam = await getData(`/teams/${team.id}?expand[]=owner`);
        setApiTeamData(currentTeam);

        if (currentTeam?.bannerFileId) {
          await fetchFileImage(currentTeam.bannerFileId, setPhoto);
        } else if (currentTeam?.profileFileId) {
          await fetchFileImage(currentTeam.profileFileId, setPhoto);
        } else {
          setPhoto(null);
        }
      } catch (err) {
        console.error("Error fetching team details and image:", err);
        setPhoto(null);
        setApiTeamData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamDetailsAndImage();
  }, [team?.id]);

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <CardActionArea
          href={
            isAdmin === true || organize
              ? `/organize/team/${team.id}`
              : `/team/${team.id}`
          }
          target={appMode ? "_blank" : "_self"}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 194
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <CardMedia
              component="img"
              height="194"
              image={
                photo?.url ||
                `${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/images/RankingsImage${
                  theme?.palette?.mode === "dark" ? "Dark" : ""
                }.png`
              }
              alt={team?.name}
            />
          )}
          <CardHeader
            title={team?.name}
            sx={{
              "& .MuiCardHeader-title": {
                fontWeight: highlight && "600"
              }
            }}
          />
        </CardActionArea>
        <CardContent>
          {team.owner && (
            <Typography gutterBottom>
              <Box
                sx={{
                  minWidth: "35px",
                  verticalAlign: "bottom",
                  display: "inline-flex"
                }}
              >
                <Tooltip
                  title={t("teamOwner", {
                    defaultValue: "Team Owner",
                    keyPrefix: "components.TeamCard.tooltips"
                  })}
                >
                  <BCPUser
                    style={{
                      color: theme?.palette?.text?.secondary,
                      "vertical-align": "bottom"
                    }}
                  />
                </Tooltip>
              </Box>
              {loading ? (
                <CircularProgress
                  size={20}
                  sx={{ ml: 1, verticalAlign: "bottom" }}
                />
              ) : apiTeamData?.owner?.firstName &&
                apiTeamData?.owner?.lastName ? (
                userDisplayName(
                  apiTeamData.owner.firstName,
                  apiTeamData.owner.lastName
                )
              ) : (
                t("ownerDetailsNotAvailable", {
                  defaultValue: "Owner details not available",
                  keyPrefix: "components.TeamCard"
                })
              )}
            </Typography>
          )}
          {(removeMember || setTeamAsDefault) && (
            <Grid container spacing={1}>
              {removeMember && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={removeMember}
                  >
                    {t("leaveTeam", {
                      defaultValue: "Leave Team"
                    })}
                  </Button>
                </Grid>
              )}
              {setTeamAsDefault && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={setTeamAsDefault}
                  >
                    {t("setAsDefault", {
                      defaultValue: "Set as default"
                    })}
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>
    </>
  );
}
